<template>
  <div>
    <DeleteGroupModal
      ref="deleteGroupModal"
      :group="group"
      :show="showingDeleteGroupModal"
      @onClose="showingDeleteGroupModal = false"
      @onDeleted="handleDeleteGroupModalDeleted" />
    <TWModal
      ref="editGroupModal"
      :title="$t('groups.editGroupTitle')"
      :loading="loading"
      :show="showingEditGroupModal"
      @close="close">
      <template v-slot:content>
        <form data-vv-scope="group-edit">
          <div class="mb-4">
            <label class="mb-1 inline-block" for="user-name">{{ $t('groups.name') }}</label>
            <TextInput
              id="group-name"
              :placeholder="$t('teams.teamNamePlaceholder')"
              v-model="group.name"
              :has-error="errors.has('group-edit.name')"
              data-ec="edit_group_modal"
              data-et="inp_group_name"
              v-validate="'required'"
              data-vv-name="name"
              data-vv-as="name"
              data-hj-suppress />
            <ValidationError v-show="errors.has('group-edit.name')">
              <template v-slot:text>
                {{ errors.first('group-edit.name') }}
              </template>
            </ValidationError>
          </div>
          <div class="mb-4">
            <label class="mb-1 inline-block" for="user-name">{{ $t('groups.add-members-to-team') }}</label>
            <UserSelectInput
                v-model="group.users"
                :hidePills="false"
                @select="handleUserSelected"
                @remove="handleUserRemoved"
            ></UserSelectInput>
          </div>
        </form>
        <div v-if="group && group.users" class="border border-0 border-t">
          <ListItem @remove="removeItem(user.id)" v-for="user in group.users" :key="user.id">
            <template v-slot:name>
              <div class="w-listItemTitle">{{ user.name }}</div>
            </template>
            <template v-slot:extraContent>
              <div class="text-14 text-lightGray font-normal">{{ user.email }}</div>
            </template>
          </ListItem>
        </div>
      </template>
      <template v-slot:controls>
        <button
            type="button"
            id="delete-group-button"
            class="tw-button tw-button-error mr-base"
            @click="handleShowDeleteModal"
            data-ec="edit_group_modal"
            data-et="btn_delete">
            <TrashIcon />
          <span>{{ $t('teams.deleteTeam') }}</span>
        </button>
        <button
            type="button"
            id="update-group-button"
            class="tw-button tw-button-cta"
            :class="{'tw-button-disabled': ! isDirty}"
            @click="handleUpdateGroup"
            data-ec="edit_group_modal"
            data-et="btn_save"
            :disabled="! isDirty">
          <CheckIcon />
          <span>{{ $t('teams.saveTeam') }}</span>
        </button>
      </template>
    </TWModal>
  </div>
</template>

<script>
import TWModal from '@/components/tailwind/Modal.vue';
import TextInput from '@/components/tailwind/forms/TextInput.vue';
import ValidationError from '@/components/tailwind/forms/ValidationError.vue';
import ListItem from '@/components/tailwind/ListItem.vue';
import DeleteGroupModal from '@/views/modals/group/DeleteGroupModal.vue';
import UserSelectInput from '@/views/components/UserSelectInput.vue';
import { logAmplitudeEvent } from '@/utils/analytics';
import { cloneDeep, isEqual } from 'lodash';
import { CheckIcon, TrashIcon } from '@/components/icons/fontIcons';

export default {
  name: 'EditGroupModal',
  components: {
    TWModal,
    TextInput,
    ValidationError,
    ListItem,
    DeleteGroupModal,
    UserSelectInput,
    CheckIcon,
    TrashIcon,
  },
  inject: {
    $validator: '$validator',
  },
  data() {
    return {
      loading: false,
      group: {},
      showingDeleteGroupModal: false,
      originalGroup: {},
    };
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    groupId: {
      type: Number,
      required: false,
    },
  },
  beforeMount() {
    if (this.groupId) {
      this.loadGroup();
    }
  },
  computed: {
    showingEditGroupModal() {
      return this.show === true && this.showingDeleteGroupModal === false;
    },
    isDirty() {
      return isEqual(this.originalGroup, this.group) === false;
    },
  },
  methods: {
    handleUserSelected() {
      logAmplitudeEvent('edit_group_modal--added_user_to_group_via_dropdown');
    },
    handleUserRemoved() {
      logAmplitudeEvent('edit_group_modal--removed_user_from_group_via_dropdown');
    },
    handleShowDeleteModal() {
      this.showingDeleteGroupModal = true;
    },
    removeItem(userId) {
      const index = this.group.users.findIndex(user => user.id === userId);
      this.group.users.splice(index, 1);
      logAmplitudeEvent('edit_group_modal--removed_user_from_group_via_list');
    },
    loadGroup() {
      this.loading = true;

      this.$store.dispatch('groups/GET_GROUP_BY_ID', this.groupId).then(() => {
        this.$set(this, 'group', this.$store.getters['groups/group']);
        this.originalGroup = cloneDeep(this.group);
        this.loading = false;
      });
    },
    close() {
      this.$emit('close');
    },
    resetComponent() {
      this.isDirty = false;
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    handleDeleteGroupModalDeleted() {
      this.showingDeleteGroupModal = false;
      this.close();
    },
    handleUpdateGroup() {
      this.$validator.validateAll('user-edit').then((validated) => {
        if (validated) {
          this.loading = true;
          this.$store.dispatch('groups/UPDATE', {
            id: this.group.id,
            name: this.group.name,
            users: this.group.users.map(user => user.id),
          }).then(() => {
            this.$emit('groupUpdated');
          })
          .finally(() => {
            this.close();
            this.loading = false;
          });
        }
      });
    },
  },
  watch: {
    show(shouldShow) {
      if (shouldShow) {
        if (this.groupId) {
          this.loadGroup();
        }
      } else {
        this.resetComponent();
      }
    },
  },
};
</script>
